import { FC } from "react";
import { Box, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { Colors } from "../../../../configs";
import { IBoxStorageHistoryInvoice } from "../../../../typings/interfaces/responses/boxStorageResponse";
import {
  formatCashierColumn,
  formatCurrency,
  formatDate,
} from "../../../../util/common-functions";

export interface IBoxStorageInvoiceRowProps {
  data: IBoxStorageHistoryInvoice;
  lastRow: boolean;
  onViewClick(data: IBoxStorageHistoryInvoice): void;
}

const BoxStorageInvoiceRow: FC<IBoxStorageInvoiceRowProps> = ({
  data,
  lastRow,
  onViewClick,
}) => {
  const isVoidedRow = Boolean(data.isVoided);
  const isRefundedRow = Boolean(data.isRefunded);
  const isFullyPickedUp = data.pickingFilterStatus === "PICKED_UP";
  const isSpecialRow = isVoidedRow || isRefundedRow || isFullyPickedUp;

  const balanceDue = Math.max(0, (data.billAmount || 0) - (data.paidAmount ?? 0));
  const balanceColor = balanceDue > 0 ? Colors.BROWN : Colors.MAIN_GRAY;

  const getStatusInfo = () => {
    if (isRefundedRow) return { label: "Refunded", color: Colors.BROWN };
    if (isVoidedRow) return { label: "Voided", color: Colors.BROWN };
    if (data.pickingFilterStatus === "PICKED_UP") {
      return { label: "Picked Up", color: Colors.PRIMARY };
    }
    if (data.pickingFilterStatus === "PARTIALLY_PICKED_UP") {
      return { label: "Partially PU", color: "#C98900" };
    }
    return { label: "In Store", color: Colors.MAIN_GRAY };
  };
  const statusInfo = getStatusInfo();

  const cellSx = {
    background: `${Colors.SECONDARY}05`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "0.85rem 0.28rem",
    boxSizing: "border-box",
    color: Colors.MAIN_GRAY,
    borderBottom: "1px solid #E8E8E8",
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.15rem",
        flexWrap: "nowrap",
        marginBottom: "0.15rem",
        opacity: isSpecialRow ? 0.45 : 1,
      }}
    >
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "2.92857142857rem",
          maxWidth: "2.92857142857rem !important",
        }}
      >
        &nbsp;
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "6.4rem",
        }}
      >
        <Typography sx={{ color: Colors.SECONDARY, fontWeight: "bold" }}>
          {data.billId}
        </Typography>
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "6.8rem",
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>
          {formatDate(data.createdTime)}
        </Typography>
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "5.5rem",
        }}
      >
        <Typography>
          {formatCashierColumn(data.cashierName, data.pickupCashierName)}
        </Typography>
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "6.5rem",
        }}
      >
        <Typography>{data.dueDate ? formatDate(data.dueDate) : "-"}</Typography>
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "5.606rem",
        }}
      >
        <Typography sx={{ fontWeight: "bold" }}>
          {data.totalPieceCount ?? data.totalItemCount ?? "N/A"}
        </Typography>
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "4.606rem",
        }}
      >
        <Typography>{data.rackNumber || "-"}</Typography>
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "5.506rem",
        }}
      >
        <Typography sx={{ fontWeight: "bold", color: balanceColor }}>
          {formatCurrency({ amount: balanceDue, prefix: "$" })}
        </Typography>
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "6.5rem",
        }}
      >
        <Typography>
          {balanceDue === 0 && (data.paidAmount ?? 0) > 0
            ? formatDate(data.lastPaymentDate)
            : "-"}
        </Typography>
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "6.5rem",
        }}
      >
        <Typography>
          {data.lastPickupDate ? formatDate(data.lastPickupDate) : "N/A"}
        </Typography>
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1,
          minWidth: "6.2rem",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            borderRadius: "999px",
            border: `1px solid ${statusInfo.color}40`,
            backgroundColor: `${statusInfo.color}1A`,
            padding: "0.21rem 0.64rem",
          }}
        >
          <Typography
            sx={{
              color: statusInfo.color,
              fontWeight: "bold",
              fontSize: "0.85rem",
              whiteSpace: "nowrap",
            }}
          >
            {statusInfo.label}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          ...cellSx,
          margin: "0.01rem",
          flex: 1.5,
          minWidth: "5.25rem",
          maxWidth: "5.25rem !important",
          padding: "0.36rem",
          ...(lastRow ? { borderBottomRightRadius: "0.71rem" } : {}),
        }}
      >
        <Box
          onClick={() => onViewClick(data)}
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: "0.5rem",
            backgroundColor: "transparent",
            transition: "opacity 0.15s ease",
            "&:hover": { opacity: 0.7 },
          }}
        >
          <VisibilityIcon sx={{ fontSize: "1.14rem", color: Colors.SECONDARY }} />
        </Box>
      </Box>
    </Box>
  );
};

export default BoxStorageInvoiceRow;
